import SEO from '../../components/SEO'
import React, { useEffect, useState } from "react"
import { useNavigate, useParams, useLocation } from "react-router-dom"
import { readTripDataParsed, clearBookingFlowStorage, saveLastBooking, readLastBooking } from "../../utils/clientStorage"
import { slugify } from "../../utils/slugify"
import Hatchback from "../../assets/cars/hatchback.webp"
import Sedan from "../../assets/cars/sedan.webp"
import SUV from "../../assets/cars/suv.webp"
import ProgressStepper from "../../components/ProgressStepper"
import { trackBookingConversion } from "../../utils/googleAds"
import { trackPurchase } from "../../utils/analytics"

const getCarImage = (name) => {
  const n = (name || "").toLowerCase().replace(/\s+/g, "")
  if (n === "hatchback") return Hatchback
  if (n === "suv") return SUV
  return Sedan
}

const CAB_TYPE = { 1: "COMPACT", 2: "SEDAN", 3: "SUV" }

const MODEL_NAMES = {
  1: "Swift, WagonR or similar",
  2: "Dzire, Etios or similar",
  3: "Innova, Ertiga or similar",
}

// vehicle_id from the fare API is a backend DB id (49/50/51 on fleets), not the
// 1/2/3 segment, so resolve the type from the name. Without this the badge read
// "CAB" and the model line "Swift, WagonR" on every confirmation, even a Sedan
// or SUV. Mirrors components/Cards/CabCard.js.
const NAME_TO_ID = { hatchback: 1, sedan: 2, suv: 3 }
const resolveCabTypeId = (cab) =>
  NAME_TO_ID[(cab?.name || "").toLowerCase().replace(/\s+/g, "")] ||
  (cab?.vehicle_id >= 1 && cab?.vehicle_id <= 3 ? cab.vehicle_id : 1)

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return ""
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return dateStr
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
}

const formatDisplayTime = (timeStr) => {
  if (!timeStr) return ""
  if (timeStr.toLowerCase().includes("am") || timeStr.toLowerCase().includes("pm")) return timeStr
  const [h, m] = timeStr.split(":").map(Number)
  if (isNaN(h)) return timeStr
  const ampm = h >= 12 ? "PM" : "AM"
  const h12 = h % 12 || 12
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`
}

const BookingSuccess = () => {
  const navigate = useNavigate()
  const { routeSlug } = useParams()
  const location = useLocation()
  const [tripData, setTripData] = useState(null)
  const [receipt, setReceipt] = useState(null)
  const [corpState, setCorpState] = useState("idle") // idle | sending | done

  useEffect(() => {
    // Read booking details from navigation state first (passed by PaymentCard).
    // Fallback to localStorage for page refreshes or direct visits.
    const data = location.state?.tripData || readTripDataParsed()
    setTripData(data)

    // Keep a small receipt that survives clearBookingFlowStorage(), so a refresh
    // still shows the booking reference instead of an apology.
    if (data) {
      saveLastBooking({
        bookingId: location.state?.booking_id || data.booking_id,
        tripRef: data.web_trip_unique_id,
        phone: data.mobile_number || data.phone_number,
        email: data.email,
        total: data.cab?.total,
      })
    }
    setReceipt(readLastBooking())

    // Scroll to top
    window.scrollTo(0, 0)

    // ── Google Ads conversion ──
    // This previously sent send_to: "AW-.../REPLACE_WITH_CONVERSION_LABEL", // a placeholder Ads discards silently, so no booking has ever been recorded
    // as a conversion. The helper refuses to fire until a real label is set and
    // says so in the console, rather than pretending to succeed.
    trackBookingConversion({
      // parseFloat, not parseInt: ₹2,551.50 must not be reported as ₹2,551
      value: parseFloat(data?.cab?.total),
      currency: "INR",
      transactionId: data?.web_trip_unique_id,
    })

    // GA4 purchase (booking confirmed), pushed to the GTM dataLayer. Same value
    // and id as the Ads call above. GTM (Phase 2) turns this into the GA4 key
    // event and can fire the Ads conversion from it too. Best-effort, never throws.
    trackPurchase({
      value: parseFloat(data?.cab?.total),
      currency: "INR",
      transactionId: data?.web_trip_unique_id,
      route: routeSlug,
    })

    // Clear localStorage when leaving this page so user can't re-trigger payment on back button.
    return () => {
      clearBookingFlowStorage()
    }
  }, [location.state])

  const cab = tripData?.cab
  const cabTypeId = resolveCabTypeId(cab)
  const cabType = CAB_TYPE[cabTypeId] || "CAB"

  // ── what was actually charged, vs what is still owed ──────────────────────
  // parseFloat, never parseInt: an advance of ₹609.60 truncated to ₹609 by
  // parseInt would under-report the balance by the rounding every single time.
  const fareTotal = Math.round(parseFloat(cab?.total) || 0)
  const advancePct = Number(cab?.advance_booking) || 20
  // Prefer the amount the gateway actually took; fall back to the percentage
  // only when the receipt did not carry it.
  const advancePaid = Math.round(
    parseFloat(tripData?.payment_amount ?? tripData?.advance_booking_amount) ||
      (fareTotal * advancePct) / 100
  )
  const balanceDue = Math.max(0, fareTotal - advancePaid)
  const displayName = cab?.name === "Hatchback" ? "Hatch Back" : cab?.name

  // Validate URL matches the stored trip to prevent fake visits
  const expectedSlug = (() => {
    if (!tripData?.pickup_city) return null
    const from = slugify(tripData.pickup_city)
    const to = tripData.drop_city ? slugify(tripData.drop_city) : null
    return to ? `${from}-to-${to}` : `${from}-local`
  })()

  const isValidVisit = expectedSlug && routeSlug === expectedSlug

  // The reference ops sees in Trip Bookings (ORD…). Falls back to the trip token
  // when the backend doesn't return one, a real reference the desk can still
  // search by beats inventing a friendly-looking number.
  const bookingRef = receipt?.bookingId || tripData?.booking_id || null
  const tripRef = receipt?.tripRef || tripData?.web_trip_unique_id || null
  const shownRef = bookingRef || tripRef
  const notifyPhone = receipt?.phone || tripData?.mobile_number || tripData?.phone_number || null
  const notifyEmail = receipt?.email || tripData?.email || null

  /**
   * "We also do corporate cabs, interested?"
   *
   * Flags the booking for the corporate desk, then sends them to the corporate
   * page. The redirect does NOT wait on the request succeeding: the customer
   * asked to see the page, and a slow network is no reason to strand them.
   */
  const handleCorporateYes = async () => {
    if (corpState !== "idle") return
    setCorpState("sending")
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/website/corporate_interest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ booking_id: bookingRef, visitorEntryId: tripData?.visitorEntryId }),
      })
    } catch (_) {
      /* logged nowhere on purpose, never block the customer on analytics */
    }
    setCorpState("done")
    navigate("/corporate-cab-services")
  }

  return (
    <div className="bg-[#f9fafa] text-[#171d1b] min-h-screen font-body pb-24">
      {/* Session-scoped booking step; no head tags of its own, so without
          this it inherits the SPA fallback's (the homepage's). */}
      <SEO title="Booking Confirmed" description="Your Pulpit cab booking is confirmed." noindex />
      {/* Header */}
      <nav className="w-full bg-white border-b border-gray-100 flex items-center justify-between px-6 md:px-12 h-24 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
        <button onClick={() => navigate("/")} className="flex items-center">
          <img src="/images/Newlogo.webp" alt="Pulpit Cabs" className="h-[54px] w-auto" />
        </button>
        <a href="tel:+918052080501" className="flex items-center gap-3 hover:opacity-80 transition-opacity duration-200">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#21A5A0" strokeWidth="2">
            <path d="M15.05 5A5 5 0 0 1 19 8.95M15.05 1A9 9 0 0 1 23 8.94m-1 7.98v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
          </svg>
          <div className="flex flex-col">
            <span className="text-[10px] text-[#6b7280] font-bold uppercase tracking-wider leading-tight">Need Help?</span>
            <span className="text-lg font-black text-[#171d1b] leading-tight tracking-tight">+91 80520 80501</span>
          </div>
        </a>
      </nav>

      {/* Progress Stepper */}
      <ProgressStepper currentStep={4} />

      {/* Premium success hero, same teal gradient + wave art as the /bookCab
          step, closing the funnel on a celebratory note; the booking-details
          card below lifts over its lower edge. */}
      <header className="relative overflow-hidden bg-gradient-to-br from-[#23aaa5] via-[#1f9c98] to-[#16807c] text-white">
        <svg
          aria-hidden="true"
          className="absolute inset-0 h-full w-full pointer-events-none"
          preserveAspectRatio="xMidYMid slice"
          viewBox="0 0 1200 340"
          fill="none"
        >
          <path d="M0 232 Q 300 132 600 208 T 1200 190 V340 H0 Z" fill="#ffffff" fillOpacity="0.09" />
          <path d="M0 270 Q 350 190 720 258 T 1200 250 V340 H0 Z" fill="#ffffff" fillOpacity="0.07" />
          <circle cx="1060" cy="72" r="140" fill="#ffffff" fillOpacity="0.05" />
          <circle cx="140" cy="40" r="90" fill="#ffffff" fillOpacity="0.045" />
        </svg>

        <div className="relative max-w-[720px] mx-auto px-4 md:px-8 pt-10 md:pt-14 pb-20 md:pb-32 flex flex-col items-center text-center">
          {/* Check badge */}
          <div className="w-[72px] h-[72px] md:w-24 md:h-24 rounded-full bg-white/15 backdrop-blur-sm border border-white/30 flex items-center justify-center mb-5 md:mb-6 shadow-[0_8px_30px_rgba(0,0,0,0.14)]">
            <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>

          {/* Eyebrow */}
          <span className="text-[11px] md:text-[12px] font-black uppercase tracking-[0.2em] text-white/85 mb-3">
            Thanks for booking
          </span>

          {/* Title */}
          <h1 className="text-[30px] md:text-[46px] font-black tracking-tight leading-[1.03] mb-4 md:mb-5">
            You&apos;re all <span className="text-[#ffd7cf]">set</span>
          </h1>

          {shownRef && (
            <div className="inline-flex items-center gap-2 bg-white rounded-full pl-4 pr-2 py-2 mb-4 shadow-[0_6px_20px_rgba(0,0,0,0.14)]">
              <span className="text-[11px] font-black text-[#6b7280] uppercase tracking-wider">Booking ID</span>
              <span className="text-[14px] font-black text-[#1a1a1a] tracking-wide font-mono bg-[#E8F5F4] rounded-full px-3 py-1">
                {shownRef}
              </span>
            </div>
          )}

          <p className="text-[14px] md:text-[16px] font-medium text-white/90 max-w-[520px]">
            Your cab is reserved. Driver &amp; cab details will reach you{" "}
            <span className="font-bold text-white">about 2 hours before pickup</span> on{" "}
            {notifyPhone || notifyEmail ? (
              <>
                {notifyPhone && <span className="font-bold text-white">+91 {notifyPhone}</span>}
                {notifyPhone && notifyEmail && " and "}
                {notifyEmail && <span className="font-bold text-white">{notifyEmail}</span>}
                .
              </>
            ) : (
              "your registered mobile number and email address."
            )}
          </p>
        </div>
      </header>

      <main className="relative z-10 max-w-[720px] mx-auto px-4 md:px-8 -mt-12 md:-mt-24">
        {/* Booking Details Card */}
        {tripData && isValidVisit ? (
          <div className="bg-white rounded-[24px] p-6 md:p-10 shadow-[0_4px_24px_rgba(0,0,0,0.03)] border border-gray-50 mb-8">
            <div className="flex items-center gap-5 pb-6 border-b border-dashed border-gray-200">
              <div className="w-[100px] md:w-[160px] shrink-0">
                <img
                  src={getCarImage(cab?.name)}
                  alt={displayName}
                  className="w-full object-contain drop-shadow-[0_8px_16px_rgba(0,0,0,0.10)]"
                />
              </div>
              <div>
                <span className="px-2.5 py-1 border-2 border-[#21A5A0] text-[#21A5A0] font-black text-[10px] uppercase tracking-wider rounded-full">
                  {cabType}
                </span>
                <h2 className="text-[24px] md:text-[32px] font-black text-[#1a1a1a] mt-2 leading-none">{displayName}</h2>
                <div className="text-[11px] font-black text-[#21A5A0] uppercase tracking-widest mt-1">
                  {MODEL_NAMES[cabTypeId] || "Swift, WagonR"}
                </div>
              </div>
            </div>

            <div className="space-y-4 mt-6">
              <div className="flex items-start gap-3.5">
                <div className="w-10 h-10 rounded-xl bg-[#f4f7f6] flex items-center justify-center shrink-0">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#21A5A0" strokeWidth="2.5">
                    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <div>
                  <div className="text-[12px] font-bold text-[#6b7280] uppercase tracking-wider">Date & Time</div>
                  <div className="text-[16px] font-black text-[#1a1a1a]">
                    {formatDisplayTime(tripData.trip_time)} | {formatDisplayDate(tripData.trip_date)}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3.5">
                <div className="w-10 h-10 rounded-xl bg-[#f4f7f6] flex items-center justify-center shrink-0">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#21A5A0" strokeWidth="2.5">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <div>
                  <div className="text-[12px] font-bold text-[#6b7280] uppercase tracking-wider">Pickup Location</div>
                  <div className="text-[16px] font-black text-[#1a1a1a]">{tripData.pickup_city}</div>
                </div>
              </div>

              {tripData.drop_city && (
                <div className="flex items-start gap-3.5">
                  <div className="w-10 h-10 rounded-xl bg-[#f4f7f6] flex items-center justify-center shrink-0">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#21A5A0" strokeWidth="2.5">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-[12px] font-bold text-[#6b7280] uppercase tracking-wider">Drop Location</div>
                    <div className="text-[16px] font-black text-[#1a1a1a]">{tripData.drop_city}</div>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3.5">
                <div className="w-10 h-10 rounded-xl bg-[#f4f7f6] flex items-center justify-center shrink-0">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#21A5A0" strokeWidth="2.5">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
                <div>
                  <div className="text-[12px] font-bold text-[#6b7280] uppercase tracking-wider">Traveller</div>
                  <div className="text-[16px] font-black text-[#1a1a1a]">
                    {tripData.first_name} {tripData.last_name}
                  </div>
                  <div className="text-[13px] font-medium text-[#777]">{tripData.email}</div>
                  <div className="text-[13px] font-medium text-[#777]">+91 {tripData.mobile_number || tripData.phone_number}</div>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-dashed border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[14px] font-medium text-[#555]">Base Amount</span>
                <span className="text-[14px] font-bold text-[#1a1a1a]">₹{Number(parseInt(cab?.subtotal)).toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[14px] font-medium text-[#555]">Taxes & Fees</span>
                <span className="text-[14px] font-bold text-[#1a1a1a]">₹{Number(parseInt(cab?.gst)).toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-4">
                <span className="text-[14px] font-medium text-[#555]">Total Fare</span>
                <span className="text-[16px] font-bold text-[#1a1a1a]">₹{fareTotal.toLocaleString()}</span>
              </div>

              {/* The customer pays an ADVANCE, not the fare. Labelling the full
                  amount "Total Paid" told them they owed nothing more, and the
                  first they would learn otherwise is the driver asking for
                  ₹2,438 at the drop. Show both numbers, and name who collects
                  the balance. */}
              <div className="flex items-center justify-between mt-3">
                <span className="text-[16px] font-black text-[#1a1a1a]">
                  Advance Paid
                  <span className="ml-1.5 text-[12px] font-bold text-[#21A5A0]">{advancePct}%</span>
                </span>
                <span className="text-[24px] font-black text-[#21A5A0]">₹{advancePaid.toLocaleString()}</span>
              </div>

              {balanceDue > 0 && (
                <div className="mt-4 rounded-xl bg-[#FFF8EC] border border-[#F5D9A8] px-4 py-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[14px] font-bold text-[#8A5A00]">Balance, pay the driver</span>
                    <span className="text-[18px] font-black text-[#8A5A00]">₹{balanceDue.toLocaleString()}</span>
                  </div>
                  <div className="mt-1 text-[12px] font-medium text-[#A07830] leading-relaxed">
                    Payable directly to the driver at the end of the trip, by cash or UPI.
                    Toll, parking and state permit charges, if any, are extra.
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Fallback when no tripData exists or URL doesn't match */
          <div className="bg-white rounded-[24px] p-8 md:p-12 shadow-[0_4px_24px_rgba(0,0,0,0.03)] border border-gray-50 mb-8 text-center">
            {/* Not an error state. The customer has paid; tripData was cleared to
                stop the back button re-triggering payment. Show the reference
                and where updates are going, not an apology. */}
            <p className="text-[15px] font-medium text-[#777] mb-2">
              Your booking is confirmed and our team can see it.
              {shownRef ? (
                <> Quote <span className="font-black text-[#1a1a1a] font-mono">{shownRef}</span> if you need to reach us.</>
              ) : null}
            </p>
            {/* The banner above already names the phone and email, repeating
                them here made the page read like it was covering for itself. */}
            <p className="text-[14px] font-medium text-[#777] mb-6">
              Full trip details are on their way to you.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate("/")}
                className="px-8 py-3.5 rounded-xl bg-[#21A5A0] text-white font-black text-[15px] hover:bg-[#1D8D89] transition-colors shadow-[0_4px_14px_rgba(33,165,160,0.3)]"
              >
                Book a Cab
              </button>
              <a
                href="tel:+918052080501"
                className="px-8 py-3.5 rounded-xl border-2 border-[#21A5A0] text-[#21A5A0] font-black text-[15px] hover:bg-[#21A5A0]/5 transition-colors inline-flex items-center justify-center"
              >
                Call Support
              </a>
            </div>
          </div>
        )}

        {/* Other queries + corporate cross-sell. Placed after the booking is
            settled, so it reads as an offer rather than an upsell mid-payment. */}
        <div className="bg-white rounded-[24px] p-6 md:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.03)] border border-gray-50 mb-8">
          <p className="text-[14px] font-medium text-[#777] mb-5 text-center">
            Any other query? Call us on{" "}
            <a href="tel:+918052080502" className="font-black text-[#21A5A0] whitespace-nowrap">+91 80520 80502</a>
          </p>

          <div className="border-t border-dashed border-gray-200 pt-5 text-center">
            <h3 className="text-[17px] md:text-[19px] font-black text-[#1a1a1a] mb-1.5">
              We also provide corporate cabs
            </h3>
            <p className="text-[14px] font-medium text-[#777] mb-5 max-w-[460px] mx-auto">
              Employee transport, airport transfers and monthly contracts for companies.
              Looking for any of these? We&apos;ll connect you with our corporate team.
            </p>
            <button
              onClick={handleCorporateYes}
              disabled={corpState !== "idle"}
              className="px-10 py-3.5 rounded-xl bg-[#21A5A0] text-white font-black text-[15px] hover:bg-[#1D8D89] transition-colors shadow-[0_4px_14px_rgba(33,165,160,0.3)] disabled:opacity-60"
            >
              {corpState === "sending" ? "One moment…" : "Yes, I'm interested"}
            </button>
          </div>
        </div>

        {/* Support Footer */}
        <div className="text-center">
          <p className="text-[13px] font-medium text-[#888] mb-4">
            Having trouble? Reach us at <a href="mailto:Support@pulpitcabs.com" className="text-[#21A5A0] font-bold underline">Support@pulpitcabs.com</a>
          </p>
          <button
            onClick={() => navigate("/")}
            className="text-[13px] font-bold text-[#21A5A0] hover:text-[#1D8D89] underline transition-colors"
          >
            Back to Home
          </button>
        </div>
      </main>
    </div>
  )
}

export default BookingSuccess
